import type { Command } from '../core/commands/types';
import { ADJUSTMENTS } from '../core/filters';
import { FILE_INPUT_ACCEPT } from '../core/io/formats';
import { getLayer } from '../core/model/document';
import { countLeaves } from '../core/model/tree';
import { THEME_OPTIONS } from '../core/theme';
import { TOOLS } from '../core/tools';
import { planCrop, planFlip, planRotate90 } from '../core/transforms';
import { nextZoomStep } from '../core/view';
import { pickImageFile } from '../io/imageIO';
import { adjustmentTarget, startAdjustment } from './adjust';
import { type AppContext, appCommands, appContext } from './context';
import { copyCurrentImage, openAnyFile } from './documentIO';
import { groupActiveLayer, ungroupActiveLayer } from './groups';
import {
  addNewLayer,
  deleteActiveLayer,
  duplicateActiveLayer,
  flattenVisible,
  hasMovableSelection,
  mergeDownActive,
  moveActiveLayer,
  nudgeActiveLayer,
} from './layers';
import {
  addMask,
  applyMaskToLayer,
  invertActiveMask,
  leaveMaskEditing,
  maskTarget,
  removeActiveMask,
  toggleMaskEditing,
  toggleMaskEnabled,
} from './masks';
import { openProjectFile, saveProject } from './projectIO';
import { rasteriseActiveLayer } from './rasterise';
import { runTransform } from './transforms';

export type { AppContext } from './context';
export { appCommands, appContext } from './context';

type Cmd = Command<AppContext>;

const ZOOM_MIN_HINT = 'Zoom is already at its minimum';
const ZOOM_MAX_HINT = 'Zoom is already at its maximum';

const fileCommands: Cmd[] = [
  {
    id: 'file.new',
    title: 'New…',
    menu: 'file',
    section: 0,
    order: 10,
    shortcut: 'mod+n',
    description: 'Create a blank 800×600 canvas',
    run: (ctx) => {
      ctx.store.getState().newDocument(800, 600, '#ffffff');
      ctx.notify('New 800×600 canvas');
    },
  },
  {
    id: 'file.open',
    title: 'Open…',
    menu: 'file',
    section: 0,
    order: 20,
    shortcut: 'mod+o',
    description: 'Open an image or a .webpaint project',
    run: async (ctx) => {
      const file = await pickImageFile(`${FILE_INPUT_ACCEPT},.webpaint`);
      if (!file) return;
      const outcome = await openAnyFile(file, ctx);
      if (!outcome.ok) {
        if (outcome.reason !== 'Cancelled') {
          ctx.notify(outcome.reason ?? 'Could not open that file', 'error');
        }
        return;
      }
      ctx.notify(
        outcome.downscaled
          ? `Opened ${outcome.document?.name} (resized to fit)`
          : `Opened ${outcome.document?.name}`,
      );
    },
  },
  {
    id: 'file.openProject',
    title: 'Open Project…',
    menu: 'file',
    section: 0,
    order: 30,
    shortcut: 'mod+alt+o',
    description: 'Open a saved .webpaint project',
    run: async (ctx) => {
      const file = await pickImageFile('.webpaint');
      if (!file) return;
      try {
        await openProjectFile(file, ctx);
        ctx.notify(`Opened ${ctx.store.getState().doc.name}`);
      } catch (error) {
        ctx.notify(error instanceof Error ? error.message : 'Could not open that project', 'error');
      }
    },
  },
  {
    id: 'file.saveProject',
    title: 'Save Project',
    menu: 'file',
    section: 1,
    order: 10,
    shortcut: 'mod+s',
    description: 'Save the layer stack as a .webpaint file',
    run: async (ctx) => {
      try {
        const fileName = await saveProject(ctx);
        ctx.notify(`Saved ${fileName}`);
      } catch (error) {
        ctx.notify(error instanceof Error ? error.message : 'Could not save the project', 'error');
      }
    },
  },
  {
    id: 'file.export',
    title: 'Export…',
    menu: 'file',
    section: 1,
    order: 20,
    shortcut: 'mod+shift+e',
    description: 'Export a flattened PNG, JPEG or WebP',
    run: (ctx) => ctx.store.getState().setExportDialog(true),
  },
  {
    id: 'file.batchExport',
    title: 'Batch Export…',
    menu: 'file',
    section: 1,
    order: 30,
    description: 'Export every visible layer — and the flattened image — in one go',
    // The targets-and-naming dialog design landed in Iteration 15
    // (docs/DESIGN-iteration-15.md §4), with Chrome's multiple-download
    // prompt written down before any code (§4.4).
    run: (ctx) => ctx.store.getState().setBatchExportDialog(true),
  },
];

const editCommands: Cmd[] = [
  {
    id: 'edit.undo',
    title: 'Undo',
    menu: 'edit',
    section: 0,
    order: 10,
    shortcut: 'mod+z',
    isEnabled: (ctx) => ctx.store.getState().canUndo,
    run: (ctx) => {
      if (!ctx.store.getState().undo()) ctx.notify('Nothing to undo', 'warn');
    },
  },
  {
    id: 'edit.redo',
    title: 'Redo',
    menu: 'edit',
    section: 0,
    order: 20,
    shortcut: ['mod+shift+z', 'mod+y'],
    isEnabled: (ctx) => ctx.store.getState().canRedo,
    run: (ctx) => {
      if (!ctx.store.getState().redo()) ctx.notify('Nothing to redo', 'warn');
    },
  },
  {
    id: 'edit.selectAll',
    title: 'Select All',
    menu: 'edit',
    section: 1,
    order: 0,
    shortcut: 'mod+a',
    description: 'Select the whole image',
    run: (ctx) => {
      const { doc, setSelection } = ctx.store.getState();
      setSelection({ x: 0, y: 0, width: doc.width, height: doc.height });
    },
  },
  {
    id: 'edit.deselect',
    title: 'Deselect',
    menu: 'edit',
    section: 1,
    order: 1,
    shortcut: 'escape',
    // While a mask is being edited, Escape means "leave the mask", and one
    // command must own that key at that moment.
    isEnabled: (ctx) => {
      const state = ctx.store.getState();
      return (state.selection !== null || state.activeLayerId !== null) && !state.maskEditing;
    },
    run: (ctx) => {
      const state = ctx.store.getState();
      // Escape unwinds one thing at a time: the marquee goes first, the layer
      // selection only if there was no marquee to clear. `state` is the
      // pre-change snapshot on purpose — hadMarquee must be decided before
      // setSelection below erases it.
      const hadMarquee = state.selection !== null;
      state.setSelection(null);
      if (!hadMarquee) state.setActiveLayer(null);
    },
  },
  {
    id: 'edit.copyImage',
    title: 'Copy Image to Clipboard',
    menu: 'edit',
    section: 1,
    order: 5,
    shortcut: 'mod+shift+c',
    description: 'Copy the flattened image as PNG',
    run: async (ctx) => {
      await copyCurrentImage(ctx);
      ctx.notify('Image copied to clipboard');
    },
  },
];

const viewCommands: Cmd[] = [
  {
    id: 'view.zoomIn',
    title: 'Zoom In',
    menu: 'view',
    section: 0,
    order: 10,
    shortcut: ['mod+equal', 'mod+plus'],
    isEnabled: (ctx) => ctx.store.getState().viewport.zoom < 64,
    description: ZOOM_MAX_HINT,
    run: (ctx) => {
      const { viewport, zoomTo } = ctx.store.getState();
      zoomTo(nextZoomStep(viewport.zoom, 1));
    },
  },
  {
    id: 'view.zoomOut',
    title: 'Zoom Out',
    menu: 'view',
    section: 0,
    order: 20,
    shortcut: 'mod+minus',
    isEnabled: (ctx) => ctx.store.getState().viewport.zoom > 0.02,
    description: ZOOM_MIN_HINT,
    run: (ctx) => {
      const { viewport, zoomTo } = ctx.store.getState();
      zoomTo(nextZoomStep(viewport.zoom, -1));
    },
  },
  {
    id: 'view.zoomFit',
    title: 'Fit in Window',
    menu: 'view',
    section: 0,
    order: 30,
    shortcut: 'mod+0',
    run: (ctx) => ctx.store.getState().zoomToFit(),
  },
  {
    id: 'view.zoomActual',
    title: 'Actual Size (100%)',
    menu: 'view',
    section: 0,
    order: 40,
    shortcut: 'mod+1',
    run: (ctx) => ctx.store.getState().zoomToActualSize(),
  },
  {
    id: 'view.toggleGrid',
    title: 'Show Grid',
    menu: 'view',
    section: 1,
    order: 10,
    toggle: true,
    isChecked: (ctx) => ctx.store.getState().showGrid,
    run: (ctx) => ctx.store.getState().toggleGrid(),
  },
  {
    id: 'view.toggleRulers',
    title: 'Show Rulers',
    menu: 'view',
    section: 1,
    order: 20,
    toggle: true,
    isChecked: (ctx) => ctx.store.getState().showRulers,
    run: (ctx) => ctx.store.getState().setShowRulers(!ctx.store.getState().showRulers),
  },
  {
    id: 'view.toggleSidebar',
    title: 'Show Sidebar',
    menu: 'view',
    section: 1,
    order: 30,
    // `mod+backslash` is Figma's toggle-UI muscle memory; plain backslash stays
    // as an alias. Tab (Photoshop's binding) was rejected on purpose: it would
    // break browser focus navigation. See DESIGN-iteration-13.md §3.
    shortcut: ['mod+backslash', 'backslash'],
    toggle: true,
    description: 'Give the canvas the panel’s width — one keystroke brings it back',
    isChecked: (ctx) => ctx.store.getState().rightPanelOpen,
    run: (ctx) => ctx.store.getState().toggleRightPanel(),
  },
  // Theme choice is a radio group, not a toggle: three entries with checkmarks
  // say what the alternatives are, which a single "toggle theme" item cannot.
  ...THEME_OPTIONS.map((option, index) => ({
    id: `view.theme.${option.id}`,
    title: option.label,
    menu: 'view' as const,
    section: 2,
    order: index * 10,
    toggle: true,
    description: option.hint,
    isChecked: (ctx: AppContext) => ctx.store.getState().theme === option.id,
    run: (ctx: AppContext) => ctx.store.getState().setTheme(option.id),
  })),
];

const imageCommands: Cmd[] = [
  {
    id: 'image.resize',
    title: 'Resize Image…',
    menu: 'image',
    section: 0,
    order: 10,
    shortcut: 'mod+r',
    run: (ctx) => ctx.store.getState().setResizeDialog('image'),
  },
  {
    id: 'image.resizeCanvas',
    title: 'Resize Canvas…',
    menu: 'image',
    section: 0,
    order: 20,
    run: (ctx) => ctx.store.getState().setResizeDialog('canvas'),
  },
  {
    id: 'image.crop',
    title: 'Crop to Selection',
    menu: 'image',
    section: 0,
    order: 30,
    shortcut: 'mod+shift+x',
    isEnabled: (ctx) => ctx.store.getState().selection !== null,
    run: (ctx) => {
      const { doc, selection } = ctx.store.getState();
      void runTransform(ctx, selection ? planCrop(doc, selection) : null);
    },
  },
  {
    id: 'image.rotate90',
    title: 'Rotate 90° Clockwise',
    menu: 'image',
    section: 1,
    order: 10,
    shortcut: 'mod+shift+r',
    run: (ctx) => {
      void runTransform(ctx, planRotate90(ctx.store.getState().doc, 1));
    },
  },
  {
    id: 'image.rotate90ccw',
    title: 'Rotate 90° Counter-Clockwise',
    menu: 'image',
    section: 1,
    order: 20,
    shortcut: 'mod+alt+r',
    run: (ctx) => {
      void runTransform(ctx, planRotate90(ctx.store.getState().doc, -1));
    },
  },
  {
    id: 'image.flipHorizontal',
    title: 'Flip Horizontal',
    menu: 'image',
    section: 1,
    order: 30,
    shortcut: 'mod+shift+h',
    run: (ctx) => {
      void runTransform(ctx, planFlip(ctx.store.getState().doc, 'horizontal'));
    },
  },
  {
    id: 'image.flipVertical',
    title: 'Flip Vertical',
    menu: 'image',
    section: 1,
    order: 40,
    shortcut: 'mod+shift+v',
    run: (ctx) => {
      void runTransform(ctx, planFlip(ctx.store.getState().doc, 'vertical'));
    },
  },
];

/**
 * Arrow-key nudges for the move tool.
 *
 * Deliberately not in the Layer menu — four "Nudge" rows would be noise; they
 * exist for the keyboard and the palette. They claim the arrows only while the
 * move tool is active, so every other tool keeps its arrow-free status quo.
 */
const NUDGE_DIRECTIONS = [
  { id: 'up', dx: 0, dy: -1, key: 'arrowup', title: 'Nudge Up' },
  { id: 'down', dx: 0, dy: 1, key: 'arrowdown', title: 'Nudge Down' },
  { id: 'left', dx: -1, dy: 0, key: 'arrowleft', title: 'Nudge Left' },
  { id: 'right', dx: 1, dy: 0, key: 'arrowright', title: 'Nudge Right' },
] as const;

const nudgeCommands: Cmd[] = NUDGE_DIRECTIONS.flatMap(({ id, dx, dy, key, title }) =>
  [false, true].map((coarse) => ({
    id: `layer.nudge.${id}${coarse ? '.coarse' : ''}`,
    title: coarse ? `${title} (10 px)` : `${title} (1 px)`,
    order: coarse ? 2 : 1,
    shortcut: coarse ? `shift+${key}` : key,
    description: `Move the selected layers ${coarse ? 10 : 1} px`,
    isEnabled: (ctx: AppContext) => {
      const state = ctx.store.getState();
      // The same gate the nudge itself applies: the selection — groups
      // expanded to their leaves (Iteration 15) — has something movable.
      return (
        state.activeTool === 'move' && state.activeLayerId !== null && hasMovableSelection(ctx)
      );
    },
    run: (ctx: AppContext) => nudgeActiveLayer(ctx, dx * (coarse ? 10 : 1), dy * (coarse ? 10 : 1)),
  })),
);

const layerCommands: Cmd[] = [
  {
    id: 'layer.new',
    title: 'New Layer',
    menu: 'layer',
    section: 0,
    order: 10,
    shortcut: 'mod+shift+n',
    run: (ctx) => addNewLayer(ctx),
  },
  {
    id: 'layer.duplicate',
    title: 'Duplicate Layer',
    menu: 'layer',
    section: 0,
    order: 20,
    shortcut: 'mod+j',
    isEnabled: (ctx) => ctx.store.getState().activeLayerId !== null,
    run: (ctx) => duplicateActiveLayer(ctx),
  },
  {
    id: 'layer.delete',
    title: 'Delete Layer',
    menu: 'layer',
    section: 0,
    order: 30,
    shortcut: 'mod+shift+backspace',
    isEnabled: (ctx) =>
      ctx.store.getState().activeLayerId !== null &&
      countLeaves(ctx.store.getState().doc.layers) > 1,
    run: (ctx) => deleteActiveLayer(ctx),
  },
  {
    id: 'layer.raise',
    title: 'Raise Layer',
    menu: 'layer',
    section: 1,
    order: 10,
    shortcut: 'alt+]',
    run: (ctx) => moveActiveLayer(ctx, 1),
  },
  {
    id: 'layer.lower',
    title: 'Lower Layer',
    menu: 'layer',
    section: 1,
    order: 20,
    shortcut: 'alt+[',
    run: (ctx) => moveActiveLayer(ctx, -1),
  },
  ...nudgeCommands,
  {
    id: 'layer.mergeDown',
    title: 'Merge Down',
    menu: 'layer',
    section: 2,
    order: 10,
    shortcut: 'mod+e',
    isEnabled: (ctx) => countLeaves(ctx.store.getState().doc.layers) > 1,
    run: (ctx) => void mergeDownActive(ctx),
  },
  {
    id: 'layer.flatten',
    title: 'Merge Visible',
    menu: 'layer',
    section: 2,
    order: 20,
    shortcut: 'mod+shift+f',
    isEnabled: (ctx) => countLeaves(ctx.store.getState().doc.layers) > 1,
    run: (ctx) => void flattenVisible(ctx),
  },
  {
    id: 'layer.group',
    title: 'Group Layers',
    menu: 'layer',
    section: 4,
    order: 10,
    shortcut: 'mod+g',
    description: 'Put the selected layers in a group — one layer takes the one below it',
    isEnabled: (ctx) => ctx.store.getState().activeLayerId !== null,
    run: (ctx) => groupActiveLayer(ctx),
  },
  {
    id: 'layer.ungroup',
    title: 'Ungroup Layers',
    menu: 'layer',
    section: 4,
    order: 20,
    shortcut: 'mod+shift+g',
    description: 'Take the active group apart',
    isEnabled: (ctx) => {
      const state = ctx.store.getState();
      const layer = state.activeLayerId ? getLayer(state.doc, state.activeLayerId) : undefined;
      return layer?.type === 'group';
    },
    run: (ctx) => ungroupActiveLayer(ctx),
  },
  {
    id: 'layer.rasterise',
    title: 'Rasterise Layer',
    menu: 'layer',
    section: 4,
    order: 30,
    description: 'Turn the layer’s description into pixels, so it can be masked',
    isEnabled: (ctx) => {
      const state = ctx.store.getState();
      const layer = state.activeLayerId ? getLayer(state.doc, state.activeLayerId) : undefined;
      return layer?.type === 'text' || layer?.type === 'fill';
    },
    run: (ctx) => rasteriseActiveLayer(ctx),
  },
  {
    id: 'layer.adjustment',
    title: 'Adjustment Layer…',
    menu: 'layer',
    section: 4,
    order: 40,
    description: 'A layer that adjusts the ones below it, without baking them',
    // A non-destructive adjustment layer means the compositor re-runs the
    // filters on every repaint — a different performance problem and a
    // different model. The direct adjustments this editor uses make it
    // redundant, so it waits for that design, not for a menu row.
    // See docs/DESIGN-iteration-11.md §5.3. (Rescoped twice: 14 took the
    // group composite, 15 took the transforms and batch export — so the
    // marker reads 16, because a label that names a shipped iteration as
    // the future would be a lie.)
    plannedFor: 16,
    run: () => undefined,
  },
  /* Masks. A mask never touches the layer's pixels until "Apply Mask", so every
   * one of these is cheap to undo. */
  {
    id: 'layer.mask.reveal',
    title: 'Add Mask — Reveal All',
    menu: 'layer',
    section: 3,
    order: 10,
    shortcut: 'mod+alt+m',
    description: 'Mask the layer with a mask that hides nothing',
    isEnabled: (ctx) => maskTarget(ctx).target !== null,
    run: (ctx) => addMask(ctx, 'reveal'),
  },
  {
    id: 'layer.mask.hide',
    title: 'Add Mask — Hide All',
    menu: 'layer',
    section: 3,
    order: 20,
    shortcut: 'mod+shift+alt+m',
    description: 'Mask the layer so it hides everything, then paint to reveal',
    isEnabled: (ctx) => maskTarget(ctx).target !== null,
    run: (ctx) => addMask(ctx, 'hide'),
  },
  {
    id: 'layer.mask.selection',
    title: 'Add Mask from Selection',
    menu: 'layer',
    section: 3,
    order: 30,
    description: 'Mask the layer to the current selection',
    isEnabled: (ctx) => maskTarget(ctx).target !== null && ctx.store.getState().selection !== null,
    run: (ctx) => addMask(ctx, 'selection'),
  },
  {
    id: 'layer.mask.edit',
    title: 'Edit Mask',
    menu: 'layer',
    section: 3,
    order: 40,
    shortcut: 'mod+alt+e',
    toggle: true,
    description: 'Paint on the mask instead of the layer',
    isEnabled: (ctx) => layerWithMask(ctx) !== null,
    isChecked: (ctx) => {
      const layer = layerWithMask(ctx);
      return layer !== null && ctx.store.getState().maskEditing === layer.id;
    },
    run: (ctx) => toggleMaskEditing(ctx),
  },
  {
    id: 'layer.mask.leave',
    title: 'Stop Editing Mask',
    section: 3,
    order: 45,
    shortcut: 'escape',
    isEnabled: (ctx) => ctx.store.getState().maskEditing !== null,
    run: (ctx) => leaveMaskEditing(ctx),
  },
  {
    id: 'layer.mask.invert',
    title: 'Invert Mask',
    menu: 'layer',
    section: 3,
    order: 50,
    shortcut: 'mod+alt+i',
    isEnabled: (ctx) => layerWithMask(ctx) !== null,
    run: (ctx) => invertActiveMask(ctx),
  },
  {
    id: 'layer.mask.toggle',
    title: 'Toggle Mask',
    menu: 'layer',
    section: 3,
    order: 60,
    toggle: true,
    description: 'Switch the mask off without throwing it away',
    isEnabled: (ctx) => layerWithMask(ctx) !== null,
    isChecked: (ctx) => layerWithMask(ctx)?.mask?.enabled === true,
    run: (ctx) => toggleMaskEnabled(ctx),
  },
  {
    id: 'layer.mask.apply',
    title: 'Apply Mask',
    menu: 'layer',
    section: 3,
    order: 70,
    description: 'Bake the mask into the layer’s pixels and drop it',
    isEnabled: (ctx) => layerWithMask(ctx) !== null,
    run: (ctx) => void applyMaskToLayer(ctx),
  },
  {
    id: 'layer.mask.remove',
    title: 'Remove Mask',
    menu: 'layer',
    section: 3,
    order: 80,
    isEnabled: (ctx) => layerWithMask(ctx) !== null,
    run: (ctx) => removeActiveMask(ctx),
  },
];

/** The layer the mask commands can act on, and only if it actually has a mask. */
function layerWithMask(ctx: AppContext) {
  const { target } = maskTarget(ctx);
  return target?.layer.mask ? target.layer : null;
}

const helpCommands: Cmd[] = [
  {
    id: 'palette.open',
    title: 'Command Palette…',
    menu: 'help',
    section: 0,
    order: 5,
    shortcut: 'mod+k',
    description: 'Search every command in the editor',
    run: (ctx) => ctx.store.getState().setPaletteOpen(true),
  },
  {
    id: 'help.shortcuts',
    title: 'Keyboard Shortcuts',
    menu: 'help',
    section: 0,
    order: 10,
    shortcut: 'mod+slash',
    run: (ctx) => ctx.store.getState().setShowShortcuts(true),
  },
  {
    id: 'help.about',
    title: 'About WebPaint',
    menu: 'help',
    section: 0,
    order: 20,
    run: (ctx) => ctx.store.getState().setShowAbout(true),
  },
];

/** One command per tool, so tool keys go through the same registry as menus. */
const toolCommands: Cmd[] = TOOLS.map((tool) => ({
  id: `tool.${tool.id}`,
  title: tool.label,
  order: 0,
  shortcut: tool.key,
  plannedFor: tool.plannedFor,
  description: tool.hint,
  isChecked: (ctx) => ctx.store.getState().activeTool === tool.id,
  run: (ctx) => ctx.store.getState().setTool(tool.id),
}));

const adjustCommands: Cmd[] = ADJUSTMENTS.map((spec, index) => ({
  id: `adjust.${spec.kind}`,
  title: spec.label,
  menu: 'adjust',
  section: 0,
  order: index * 10,
  hint: spec.hint,
  // Enabled when the active layer can take pixels; the reason comes from the
  // same resolver the panel uses, so menu and panel refuse identically.
  isEnabled: (ctx) => adjustmentTarget(ctx).target !== null,
  run: (ctx) => {
    void startAdjustment(ctx, spec.kind);
    // A command that needs the panel reveals it (Iteration 13): over a hidden
    // sidebar, setRightPanel would open a tab nobody can see.
    ctx.store.getState().openRightPanel('adjust');
  },
}));

export const ALL_COMMANDS: readonly Cmd[] = [
  ...fileCommands,
  ...editCommands,
  ...viewCommands,
  ...imageCommands,
  ...adjustCommands,
  ...layerCommands,
  ...helpCommands,
  ...toolCommands,
];

let registered = false;

/** Idempotent: safe to call from module scope and from tests. */
export function registerCommands(): void {
  if (registered) return;
  appCommands.registerAll(ALL_COMMANDS);
  appCommands.onFailure = (reason) => appContext.notify(reason, 'warn');
  registered = true;
}

registerCommands();
